import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {BehaviorSubject, Observable} from 'rxjs';
import {Capacitor} from '@capacitor/core';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms?: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}

export type AppInstallPlatform = 'native' | 'ios' | 'android' | 'desktop' | 'unknown';

export interface AppInstallState {
  canInstall: boolean;
  isInstalled: boolean;
  isStandalone: boolean;
  platform: AppInstallPlatform;
  supportsPrompt: boolean;
  shouldShowPrimaryCta: boolean;
  shouldShowFallbackBanner: boolean;
  isChoiceModalOpen: boolean;
  isPwaInstructionsOpen: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AppInstallService {
  private static readonly FALLBACK_BANNER_DISMISS_STORAGE_KEY = 'wtg-open-in-app-cta-dismissed';
  private static readonly IOS_APP_STORE_URL = 'https://apps.apple.com/app/id6758525672';
  private static readonly ANDROID_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.cg.wtg.app';
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private readonly supportedUniversalLinkHosts = new Set([
    'watchthisgarage.com',
    'test.watchthisgarage.com'
  ]);
  private readonly stateSubject = new BehaviorSubject<AppInstallState>({
    canInstall: false,
    isInstalled: false,
    isStandalone: false,
    platform: 'unknown',
    supportsPrompt: false,
    shouldShowPrimaryCta: false,
    shouldShowFallbackBanner: false,
    isChoiceModalOpen: false,
    isPwaInstructionsOpen: false
  });

  public readonly state$: Observable<AppInstallState> = this.stateSubject.asObservable();

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly router: Router
  ) {
    this.init();
  }

  public get snapshot(): AppInstallState {
    return this.stateSubject.value;
  }

  public async promptInstall(): Promise<boolean> {
    if (!this.deferredPrompt) {
      return false;
    }

    const promptEvent = this.deferredPrompt;
    this.deferredPrompt = null;
    this.updateState({canInstall: false, supportsPrompt: false});

    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    const accepted = choice?.outcome === 'accepted';

    if (!accepted) {
      this.deferredPrompt = promptEvent;
      this.updateState({canInstall: true, supportsPrompt: true});
    }

    return accepted;
  }

  public dismissFallbackBanner(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    sessionStorage.setItem(AppInstallService.FALLBACK_BANNER_DISMISS_STORAGE_KEY, '1');
    this.syncState();
  }

  public async handlePrimaryAction(): Promise<void> {
    const state = this.snapshot;
    if (state.platform === 'desktop' || state.platform === 'unknown') {
      this.openChoiceModal();
      return;
    }

    if (state.platform !== 'android' && state.platform !== 'ios') {
      return;
    }

    const opened = await this.tryOpenNativeApp();
    if (!opened) {
      this.openChoiceModal();
    }
  }

  public async retryOpenNativeApp(): Promise<void> {
    const opened = await this.tryOpenNativeApp();
    if (!opened) {
      this.openChoiceModal();
    }
  }

  public openChoiceModal(): void {
    this.updateState({
      isChoiceModalOpen: true,
      isPwaInstructionsOpen: false
    });
  }

  public closeChoiceModal(): void {
    this.updateState({isChoiceModalOpen: false});
  }

  public closePwaInstructions(): void {
    this.updateState({isPwaInstructionsOpen: false});
  }

  public async choosePwaInstall(): Promise<void> {
    const state = this.snapshot;
    this.updateState({isChoiceModalOpen: false});

    if (state.platform === 'ios') {
      this.updateState({isPwaInstructionsOpen: true});
      return;
    }

    await this.promptInstall();
  }

  public openNativeStore(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const target = this.snapshot.platform === 'ios'
      ? AppInstallService.IOS_APP_STORE_URL
      : AppInstallService.ANDROID_PLAY_STORE_URL;
    window.location.assign(target);
  }

  private init(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (Capacitor.getPlatform() !== 'web') {
      this.stateSubject.next({
        canInstall: false,
        isInstalled: true,
        isStandalone: true,
        platform: 'native',
        supportsPrompt: false,
        shouldShowPrimaryCta: false,
        shouldShowFallbackBanner: false,
        isChoiceModalOpen: false,
        isPwaInstructionsOpen: false
      });
      return;
    }

    this.syncState();
    window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt);
    window.addEventListener('appinstalled', this.onAppInstalled);

    const standaloneMediaQuery = window.matchMedia('(display-mode: standalone)');
    standaloneMediaQuery.addEventListener('change', this.onStandaloneModeChange);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(() => this.syncState());
  }

  private readonly onBeforeInstallPrompt = (event: Event): void => {
    event.preventDefault();
    this.deferredPrompt = event as BeforeInstallPromptEvent;
    this.syncState();
  };

  private readonly onAppInstalled = (): void => {
    this.deferredPrompt = null;
    this.updateState({
      canInstall: false,
      isInstalled: true,
      isStandalone: true,
      supportsPrompt: false,
      shouldShowPrimaryCta: false,
      shouldShowFallbackBanner: false,
      isChoiceModalOpen: false,
      isPwaInstructionsOpen: false
    });
  };

  private readonly onStandaloneModeChange = (): void => {
    this.syncState();
  };

  private syncState(): void {
    const isStandalone = this.isStandalone();
    const platform = this.resolvePlatform();
    const supportsPrompt = !!this.deferredPrompt;
    const isInstalled = isStandalone;
    const canInstall = supportsPrompt && !isStandalone;
    const isWeb = Capacitor.getPlatform() === 'web';
    const isMobileWeb = isWeb && (platform === 'ios' || platform === 'android');
    const shouldShowPrimaryCta = isWeb
      && !isStandalone
      && platform !== 'native';
    const shouldShowFallbackBanner = isMobileWeb
      && !isStandalone
      && this.isSupportedUniversalLinkHost()
      && !this.isEntryRoute()
      && !this.isFallbackBannerDismissed();

    this.stateSubject.next({
      canInstall,
      isInstalled,
      isStandalone,
      platform,
      supportsPrompt,
      shouldShowPrimaryCta,
      shouldShowFallbackBanner,
      isChoiceModalOpen: this.stateSubject.value.isChoiceModalOpen,
      isPwaInstructionsOpen: this.stateSubject.value.isPwaInstructionsOpen
    });
  }

  private updateState(partial: Partial<AppInstallState>): void {
    this.stateSubject.next({
      ...this.stateSubject.value,
      ...partial
    });
  }

  private isStandalone(): boolean {
    const mediaMatch = window.matchMedia('(display-mode: standalone)').matches;
    const navigatorStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

    return mediaMatch || navigatorStandalone;
  }

  private isSupportedUniversalLinkHost(): boolean {
    return this.supportedUniversalLinkHosts.has(window.location.hostname);
  }

  private isEntryRoute(): boolean {
    const path = window.location.pathname || '/';
    return path === '/' || path === '/login' || path === '/registration';
  }

  private isFallbackBannerDismissed(): boolean {
    return sessionStorage.getItem(AppInstallService.FALLBACK_BANNER_DISMISS_STORAGE_KEY) === '1';
  }

  private resolvePlatform(): AppInstallPlatform {
    const userAgent = window.navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (isIos) {
      return 'ios';
    }

    if (/Android/i.test(userAgent)) {
      return 'android';
    }

    if (/Macintosh|Windows|Linux|CrOS/i.test(userAgent)) {
      return 'desktop';
    }

    return 'unknown';
  }

  private isIosSafari(): boolean {
    const userAgent = window.navigator.userAgent || '';

    return /Safari/i.test(userAgent)
      && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(userAgent);
  }

  private async tryOpenNativeApp(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    const customSchemeUrl = this.buildCustomSchemeUrl(window.location);

    return await new Promise<boolean>((resolve) => {
      let resolved = false;
      let fallbackTimer: number | null = null;

      const cleanup = (): void => {
        if (fallbackTimer !== null) {
          window.clearTimeout(fallbackTimer);
          fallbackTimer = null;
        }
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', onPageHide);
        window.removeEventListener('blur', onBlur);
      };

      const resolveOnce = (opened: boolean): void => {
        if (resolved) {
          return;
        }
        resolved = true;
        cleanup();
        resolve(opened);
      };

      const onVisibilityChange = (): void => {
        if (document.hidden) {
          resolveOnce(true);
        }
      };

      const onPageHide = (): void => resolveOnce(true);
      const onBlur = (): void => resolveOnce(true);

      document.addEventListener('visibilitychange', onVisibilityChange, {passive: true});
      window.addEventListener('pagehide', onPageHide, {passive: true});
      window.addEventListener('blur', onBlur, {passive: true});

      fallbackTimer = window.setTimeout(() => resolveOnce(false), 1400);
      window.location.assign(customSchemeUrl);
    });
  }

  private buildCustomSchemeUrl(location: Location): string {
    return `com.cg.wtg.app:${location.pathname || '/'}${location.search || ''}${location.hash || ''}`;
  }
}
